import * as React from 'react'
import { HiddenField } from 'react-rails-form-helpers'
import {
  BRAND_LABELS,
  CardBrandId,
  detectCardBrand,
  formatCardNumber,
  ICardBrand,
  isBrandAccepted,
  luhnCheck,
  parseExpiration,
} from './cardBrands'

// Vendored card-brand marks from datatrans/payment-logos (MIT). Imported as
// React components via svg-react-loader, matching the app's icon convention
// (see igniter/Icons). Filenames mirror the brand ids from cardBrands.ts.
import VisaIcon from '!svg-react-loader!images/igniter/svgs/card-brands/visa.svg'
import MastercardIcon from '!svg-react-loader!images/igniter/svgs/card-brands/mastercard.svg'
import AmexIcon from '!svg-react-loader!images/igniter/svgs/card-brands/amex.svg'
import DiscoverIcon from '!svg-react-loader!images/igniter/svgs/card-brands/discover.svg'
import DinersIcon from '!svg-react-loader!images/igniter/svgs/card-brands/diners.svg'
import JcbIcon from '!svg-react-loader!images/igniter/svgs/card-brands/jcb.svg'
import GenericCardIcon from '!svg-react-loader!images/igniter/svgs/card-brands/generic.svg'

const { useState, useRef, useEffect } = React

interface IDefaultValues {
  cardNumber: string
  cvv: string
  expMonth: string
  expYear: string
}

interface IProps {
  defaultValues: IDefaultValues
}

interface IErrors {
  card: string
  exp: string
  cvc: string
}

const CARD_ERROR = 'That card number doesn\'t look right. Please double-check it.'
const ACCEPTED_BRANDS_TEXT =
  'Visa, Mastercard, American Express, Discover, or JCB'

// Helpful, brand-specific message for a card we detect but don't accept.
function brandNotAcceptedError(id: CardBrandId): string {
  return `Sorry, we don't accept ${BRAND_LABELS[id]} cards. We accept ${ACCEPTED_BRANDS_TEXT}.`
}
const EXP_PAST_ERROR = 'That expiration date has passed.'
const EXP_MONTH_ERROR = 'That isn\'t a valid month.'
const EXP_INCOMPLETE_ERROR = 'Enter a complete expiration date (MM / YY).'
const CVC_SHORT_ERROR = 'CVC is too short.'

// Build the initial "MM / YY" display string from the saved card's parts.
function initialExpDisplay({ expMonth, expYear }: IDefaultValues): string {
  if (!expMonth) { return '' }
  const mm = ('0' + expMonth).slice(-2)
  const yy = expYear ? expYear.slice(-2) : ''
  return yy ? `${mm} / ${yy}` : `${mm} / `
}

// Format a free-form expiration entry into "MM / YY" as the user types.
function formatExpiration(raw: string): string {
  let digits = (raw || '').replace(/\D/g, '').slice(0, 4)

  if (digits.length === 1 && parseInt(digits, 10) > 1) {
    digits = '0' + digits
  }

  if (digits.length >= 2) {
    let mm = digits.slice(0, 2)
    const m = parseInt(mm, 10)
    if (m === 0) {
      mm = '01'
    } else if (m > 12) {
      mm = '12'
    }
    const yy = digits.slice(2)
    return yy.length ? `${mm} / ${yy}` : `${mm} / `
  }

  return digits
}

function errorsHasAnyValues(errors: IErrors): boolean {
  return Object.values(errors).some(v => v !== '')
}

export function UnifiedCardInput({ defaultValues }: IProps) {
  const [cardDisplay, setCardDisplay] = useState(() =>
    formatCardNumber(defaultValues.cardNumber, [4, 8, 12], 16),
  )
  const [expDisplay, setExpDisplay] = useState(() => initialExpDisplay(defaultValues))
  const [cvcDisplay, setCvcDisplay] = useState(defaultValues.cvv || '')
  const [errors, setErrors] = useState<IErrors>({ card: '', exp: '', cvc: '' })

  const cardRef = useRef<HTMLInputElement>(null)
  const expRef = useRef<HTMLInputElement>(null)
  const cvcRef = useRef<HTMLInputElement>(null)
  // Field to auto-advance focus to, applied after the completing keystroke
  // commits (see effect below).
  const pendingFocusRef = useRef<React.RefObject<HTMLInputElement> | null>(null)

  const cardDigits = cardDisplay.replace(/\D/g, '')
  const brand: ICardBrand = detectCardBrand(cardDigits)
  const cvcDigits = cvcDisplay.replace(/\D/g, '')

  // Re-format the card number whenever the detected brand changes (e.g. as soon
  // as enough digits are typed to switch from the default 4-4-4-4 grouping to
  // Amex 4-6-5), and re-cap to the brand's max length.
  function setCard(raw: string) {
    const detected = detectCardBrand(raw)
    setCardDisplay(formatCardNumber(raw, detected.gaps, detected.maxLength))
  }

  // --- hidden-mirror values (digits only / parsed parts) ---
  const parsedExp = parseExpiration(expDisplay)
  const expMonthValue = parsedExp ? String(parsedExp.month) : ''
  const expYearValue = parsedExp && parsedExp.year ? String(parsedExp.year) : ''

  // --- validation ---
  function validateCard(): string {
    if (cardDigits.length === 0) { return '' }
    // Reject detected-but-unaccepted brands (e.g. Diners) with a clear message,
    // before the generic length/Luhn checks.
    if (brand.id !== 'unknown' && !isBrandAccepted(brand.id)) {
      return brandNotAcceptedError(brand.id)
    }
    if (cardDigits.length < brand.minLength) { return CARD_ERROR }
    if (!luhnCheck(cardDigits)) { return CARD_ERROR }
    return ''
  }

  // Flag a partially-entered date the same way the card and CVC fields flag a
  // partial entry. Empty is left clean (the field's `required` attribute blocks
  // an empty submit) so we don't nag before the user has typed anything.
  function validateExp(): string {
    const digits = expDisplay.replace(/\D/g, '')
    if (digits.length === 0) { return '' }
    if (digits.length < 4) { return EXP_INCOMPLETE_ERROR }
    if (!parsedExp || !parsedExp.year) { return EXP_MONTH_ERROR }

    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1
    const year = parsedExp.year || 0
    if (year < currentYear || (year === currentYear && parsedExp.month < currentMonth)) {
      return EXP_PAST_ERROR
    }
    return ''
  }

  function validateCvc(): string {
    if (cvcDigits.length === 0) { return '' }
    if (cvcDigits.length !== brand.cvcLength) { return CVC_SHORT_ERROR }
    return ''
  }

  // --- change handlers (clear that field's error on edit) ---
  function onCardChange(e: React.ChangeEvent<HTMLInputElement>) {
    const detected = detectCardBrand(e.target.value)
    const newDigits = e.target.value.replace(/\D/g, '')
    // Auto-advance only on the transition into "full" so editing an already
    // complete number doesn't keep yanking focus to the expiration field.
    const justFilled = cardDigits.length < detected.maxLength && newDigits.length >= detected.maxLength
    setCard(e.target.value)
    // Surface a not-accepted brand immediately; otherwise clear as the user edits.
    const brandError = detected.id !== 'unknown' && !isBrandAccepted(detected.id)
      ? brandNotAcceptedError(detected.id)
      : ''
    setErrors((prev) => ({ ...prev, card: brandError }))
    // Defer auto-advance to the post-commit effect (see below) rather than
    // focusing here: focusing synchronously blurs this field before React has
    // re-rendered, so its blur validation would run against the stale,
    // one-digit-short value and flash a false "looks wrong" error.
    if (justFilled) { pendingFocusRef.current = expRef }
  }

  // Apply a pending auto-advance once the completing keystroke has committed,
  // so the blur it triggers on the field we're leaving validates its final value.
  useEffect(() => {
    const target = pendingFocusRef.current
    if (target && target.current) {
      pendingFocusRef.current = null
      target.current.focus()
    }
  })

  function onExpChange(e: React.ChangeEvent<HTMLInputElement>) {
    const prevDigits = expDisplay.replace(/\D/g, '')
    const newDigits = e.target.value.replace(/\D/g, '')
    const justFilled = prevDigits.length < 4 && newDigits.length >= 4
    setExpDisplay(formatExpiration(e.target.value))
    setErrors((prev) => ({ ...prev, exp: '' }))
    if (justFilled) { pendingFocusRef.current = cvcRef }
  }

  function onCvcChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCvcDisplay(e.target.value.replace(/\D/g, '').slice(0, brand.cvcLength))
    setErrors((prev) => ({ ...prev, cvc: '' }))
  }

  // Re-cap the CVC when the brand's expected length shrinks (e.g. editing the
  // card number downgrades Amex's 4-digit CVC to a 3-digit brand) so a stale
  // 4th digit doesn't trip a misleading "CVC is too short" error.
  useEffect(() => {
    setCvcDisplay((prev) => prev.slice(0, brand.cvcLength))
  }, [brand.cvcLength])

  // --- submit guard: block native form submit while any inline error is active ---
  useEffect(() => {
    const form = cardRef.current ? cardRef.current.form : null
    if (!form) { return undefined }

    const handler = (event: Event) => {
      const next: IErrors = { card: validateCard(), exp: validateExp(), cvc: validateCvc() }
      setErrors(next)
      if (next.card) {
        event.preventDefault()
        if (cardRef.current) { cardRef.current.focus() }
      } else if (next.exp) {
        event.preventDefault()
        if (expRef.current) { expRef.current.focus() }
      } else if (next.cvc) {
        event.preventDefault()
        if (cvcRef.current) { cvcRef.current.focus() }
      }
    }

    form.addEventListener('submit', handler)
    return () => form.removeEventListener('submit', handler)
  })

  const hasError = Boolean(errors.card || errors.exp || errors.cvc)

  return (
    <>
      <div className={`unified-card-input cell small-full${hasError ? ' is-invalid' : ''}`}>
        <div className="unified-card-input__card-wrap">
          <input
            ref={cardRef}
            type="text"
            id="card_number_display"
            autoComplete="cc-number"
            inputMode="numeric"
            required={true}
            placeholder="Card number"
            value={cardDisplay}
            onChange={onCardChange}
            onBlur={() => setErrors((prev) => ({ ...prev, card: validateCard() }))}
            className="unified-card-input__field unified-card-input__field--card"
          />
          <span aria-hidden="true" className="unified-card-input__brand">
            <BrandLogo brandId={cardDigits.length === 0 ? 'unknown' : brand.id} />
          </span>
        </div>

        <input
          ref={expRef}
          type="text"
          autoComplete="cc-exp"
          inputMode="numeric"
          required={true}
          placeholder="MM / YY"
          value={expDisplay}
          onChange={onExpChange}
          onBlur={() => setErrors((prev) => ({ ...prev, exp: validateExp() }))}
          className="unified-card-input__field unified-card-input__field--exp"
        />

        <input
          ref={cvcRef}
          type="text"
          autoComplete="cc-csc"
          inputMode="numeric"
          required={true}
          pattern={`[0-9]{${brand.cvcLength}}`}
          maxLength={brand.cvcLength}
          placeholder="CVC"
          value={cvcDisplay}
          onChange={onCvcChange}
          onBlur={() => setErrors((prev) => ({ ...prev, cvc: validateCvc() }))}
          className={`unified-card-input__field unified-card-input__field--cvc${
            brand.id === 'amex' ? ' unified-card-input__field--cvc-amex' : ''
          }`}
        />
      </div>

      {/* Hidden mirrors carry the canonical field names Accept.js reads. */}
      <HiddenField name="card_number" value={cardDigits} />
      <HiddenField name="exp_month" value={expMonthValue} />
      <HiddenField name="exp_year" value={expYearValue} />
      <HiddenField name="cvv" value={cvcDigits} />

      {errorsHasAnyValues(errors) ?
        <div className="unified-card-input__errors">
          {errors.card ? <FieldError message={errors.card} /> : null}
          {errors.exp ? <FieldError message={errors.exp} /> : null}
          {errors.cvc ? <FieldError message={errors.cvc} /> : null}
        </div>
        : null
      }
    </>
  )
}

function FieldError({ message }: { message: string }) {
  return <div className="unified-card-input__error">{message}</div>
}

const BRAND_LOGOS: Record<ICardBrand['id'], React.ComponentType<any>> = {
  visa: VisaIcon,
  mastercard: MastercardIcon,
  amex: AmexIcon,
  discover: DiscoverIcon,
  diners: DinersIcon,
  jcb: JcbIcon,
  unknown: GenericCardIcon,
}

function BrandLogo({ brandId }: { brandId: ICardBrand['id'] }) {
  const Logo = BRAND_LOGOS[brandId] || GenericCardIcon
  return <Logo width={34} height={22} />
}
